import { actionTypesApi } from '@/api'
import type { ActionType, UpdateActionTypeRequest } from '@/api/actionTypesApi'
import { useAuthStore } from '@/stores/authStore'
import { type PageError, toPageError } from '@/utils/errorHandler'
import { isOwnerOfOrganization } from '@/utils/permissions'
import { formatTimeoutDurationFromSeconds } from '@/utils/timeoutUtils'
import { create } from 'zustand'

export const mapOrgActionTypeNamesToActionTypes = (names: string[]): ActionType[] =>
	names.map(type => ({
		type,
		payload_type: 'json',
		active: true,
	}))

type FilterValue = string | number | boolean | null | undefined
// Action types to exclude from dropdown in execute command dialog
const EXCLUDED_ACTION_TYPES = ['cancel_action', 'launch_shell']

interface ActionTypesStore {
	// Data
	allActionTypes: ActionType[] // All fetched data
	actionTypes: ActionType[] // Paginated data
	totalCount: number

	// Pagination & Filters
	currentPage: number
	pageSize: number
	searchQuery: string
	filters: Record<string, FilterValue>

	isLoading: boolean
	error: PageError | null

	fetchActionTypes: (options?: { organizationId?: string }) => Promise<void>
	getDropdownActionTypes: () => ActionType[]
	setPage: (page: number) => void
	setPageSize: (size: number) => void
	setSearchQuery: (query: string) => void
	setFilters: (filters: Record<string, FilterValue>) => void
	clearError: () => void
	createActionType: (actionType: ActionType) => Promise<void>
	updateActionType: (action: string, actionType: UpdateActionTypeRequest) => Promise<void>
	deleteActionType: (action: string) => Promise<void>
	deactivateActionType: (action: string) => Promise<void>
	reactivateActionType: (action: string) => Promise<void>
	totalPages: number
}

// Helper function to filter and search action types
const filterAndSearchActionTypes = (
	allActionTypes: ActionType[],
	searchQuery: string,
	filters: Record<string, FilterValue>,
) => {
	let filtered = [...allActionTypes]

	// Apply search query
	if (searchQuery.trim()) {
		const query = searchQuery.toLowerCase()
		filtered = filtered.filter(
			actionType =>
				actionType.type.toLowerCase().includes(query) ||
				actionType.payload_type.toLowerCase().includes(query) ||
				actionType.timeout_seconds?.toString().includes(query) ||
				(actionType.timeout_seconds !== undefined &&
					actionType.timeout_seconds !== null &&
					formatTimeoutDurationFromSeconds(actionType.timeout_seconds).toLowerCase().includes(query)) ||
				// Search timeout_trigger (match raw value 'in_progress' or friendly 'in progress')
				actionType.timeout_trigger
					?.toLowerCase()
					.includes(query) ||
				(actionType.timeout_trigger === 'in_progress' && 'in progress'.includes(query)) ||
				// Search retry count
				actionType.retry_on_timeout_count
					?.toString()
					.includes(query),
		)
	}

	// Apply filters
	for (const [key, value] of Object.entries(filters)) {
		if (value !== undefined && value !== null && value !== '') {
			filtered = filtered.filter(actionType => {
				switch (key) {
					case 'type':
						return actionType.type.toLowerCase().includes(String(value).toLowerCase())
					case 'payload_type':
						return actionType.payload_type.toLowerCase().includes(String(value).toLowerCase())
					case 'timeout_seconds':
						return formatTimeoutDurationFromSeconds(actionType.timeout_seconds) === String(value)
					case 'timeout_trigger':
						return actionType.timeout_trigger?.toLowerCase() === String(value).toLowerCase()
					case 'retry_on_timeout_count':
						return String(actionType.retry_on_timeout_count) === String(value)
					default:
						return true
				}
			})
		}
	}

	return filtered
}

// Helper function to paginate data
const paginateData = (data: ActionType[], page: number, pageSize: number) => {
	const startIndex = (page - 1) * pageSize
	const endIndex = startIndex + pageSize
	return data.slice(startIndex, endIndex)
}

export const useActionTypesStore = create<ActionTypesStore>((set, get) => ({
	// Initial State
	allActionTypes: [],
	actionTypes: [],
	totalCount: 0,
	currentPage: 1,
	pageSize: 20,
	searchQuery: '',
	filters: {},
	isLoading: false,
	error: null,
	totalPages: 1,

	fetchActionTypes: async (options?: { organizationId?: string }) => {
		if (options?.organizationId) {
			const user = useAuthStore.getState().user
			if (!isOwnerOfOrganization(user, options.organizationId)) {
				return
			}
		}

		set({ isLoading: true, error: null })

		try {
			const response = options?.organizationId
				? mapOrgActionTypeNamesToActionTypes(['reprovision', 'attach', 'detach'])
				: await actionTypesApi.getActionTypes()
			const state = get()

			// Filter and search the data
			const filteredData = filterAndSearchActionTypes(response, state.searchQuery, state.filters)

			// Calculate pagination
			const totalCount = filteredData.length
			const totalPages = Math.ceil(totalCount / state.pageSize)
			const paginatedData = paginateData(filteredData, state.currentPage, state.pageSize)

			set({
				allActionTypes: response,
				actionTypes: paginatedData,
				totalCount,
				totalPages,
				isLoading: false,
				error: null,
			})
		} catch (error) {
			console.error('Failed to fetch action types:', error)
			set({
				isLoading: false,
				error: toPageError(error, 'Failed to fetch action types'),
			})
		}
	},

	getDropdownActionTypes: () => {
		const state = get()
		return state.allActionTypes.filter(actionType => !EXCLUDED_ACTION_TYPES.includes(actionType.type))
	},

	setPage: (page: number) => {
		const state = get()
		set({ currentPage: page })

		// Re-apply filters and pagination without fetching from API
		const filteredData = filterAndSearchActionTypes(state.allActionTypes, state.searchQuery, state.filters)
		const totalCount = filteredData.length
		const totalPages = Math.ceil(totalCount / state.pageSize)
		const paginatedData = paginateData(filteredData, page, state.pageSize)

		set({
			actionTypes: paginatedData,
			totalCount,
			totalPages,
		})
	},

	setPageSize: (size: number) => {
		const state = get()
		set({
			pageSize: size,
			currentPage: 1, // Reset to first page when changing page size
		})

		// Re-apply filters and pagination without fetching from API
		const filteredData = filterAndSearchActionTypes(state.allActionTypes, state.searchQuery, state.filters)
		const totalCount = filteredData.length
		const totalPages = Math.ceil(totalCount / size)
		const paginatedData = paginateData(filteredData, 1, size)

		set({
			actionTypes: paginatedData,
			totalCount,
			totalPages,
		})
	},

	setSearchQuery: (query: string) => {
		const state = get()
		set({ searchQuery: query, currentPage: 1 })

		// Re-apply filters and pagination without fetching from API
		const filteredData = filterAndSearchActionTypes(state.allActionTypes, query, state.filters)
		const totalCount = filteredData.length
		const totalPages = Math.ceil(totalCount / state.pageSize)
		const paginatedData = paginateData(filteredData, 1, state.pageSize)

		set({
			actionTypes: paginatedData,
			totalCount,
			totalPages,
		})
	},

	setFilters: (filters: Record<string, FilterValue>) => {
		const state = get()
		set({ filters, currentPage: 1 })

		// Re-apply filters and pagination without fetching from API
		const filteredData = filterAndSearchActionTypes(state.allActionTypes, state.searchQuery, filters)
		const totalCount = filteredData.length
		const totalPages = Math.ceil(totalCount / state.pageSize)
		const paginatedData = paginateData(filteredData, 1, state.pageSize)

		set({
			actionTypes: paginatedData,
			totalCount,
			totalPages,
		})
	},

	clearError: () => {
		set({ error: null })
	},

	createActionType: async (actionType: ActionType) => {
		set({ isLoading: true, error: null })

		try {
			await actionTypesApi.createActionType(actionType)
			// Refresh the list after creating
			await get().fetchActionTypes()
		} catch (error) {
			console.error('Failed to create action type:', error)
			set({
				isLoading: false,
				error: toPageError(error, 'Failed to create action type'),
			})
		}
	},

	updateActionType: async (action: string, actionType: UpdateActionTypeRequest) => {
		set({ isLoading: true, error: null })

		try {
			await actionTypesApi.updateActionType(action, actionType)
			// Refresh the list after updating
			await get().fetchActionTypes()
		} catch (error) {
			console.error('Failed to update action type:', error)
			set({
				isLoading: false,
				error: toPageError(error, 'Failed to update action type'),
			})
		}
	},

	deleteActionType: async (action: string) => {
		set({ isLoading: true, error: null })

		try {
			await actionTypesApi.deleteActionType(action)
			// Refresh the list after deleting
			await get().fetchActionTypes()
		} catch (error) {
			console.error('Failed to delete action type:', error)
			set({
				isLoading: false,
				error: toPageError(error, 'Failed to delete action type'),
			})
		}
	},

	deactivateActionType: async (action: string) => {
		set({ isLoading: true, error: null })

		try {
			await actionTypesApi.deactivateActionType(action)
			await get().fetchActionTypes()
		} catch (error) {
			console.error('Failed to deactivate action type:', error)
			set({
				isLoading: false,
				error: toPageError(error, 'Failed to deactivate action type'),
			})
			throw error
		}
	},

	reactivateActionType: async (action: string) => {
		set({ isLoading: true, error: null })

		try {
			await actionTypesApi.reactivateActionType(action)
			await get().fetchActionTypes()
		} catch (error) {
			console.error('Failed to reactivate action type:', error)
			set({
				isLoading: false,
				error: toPageError(error, 'Failed to reactivate action type'),
			})
			throw error
		}
	},
}))
